<template>
  <div class="ag-grid" style="width: 100%">
    <ag-grid-vue
      class="ag-theme-balham default-grid"
      :column-defs="columnDefs"
      :row-data="rowData"
      :pagination="activeTabRows > pageLimit"
      :pagination-page-size="pageLimit"
      :grid-options="gridOptions"
      :key="gridKey"
      :components="components"
      :row-key="rowKey"
      :row-selection="'multiple'"
      :suppress-row-click-selection="true"
      :suppressPaginationPanel="true"
      @row-selected="handleSelectionChange"
    >
    </ag-grid-vue>
  </div>
</template>

<script>
import { formatLocale } from '@/utils/helpers/StringsNumbers'
import { AgGridVue } from 'ag-grid-vue3'
import 'ag-grid-enterprise'
import AgGridColumnMethods from '@/Mixins/SigmaClientNew/AgGridColumnMethods'
import ButtonCellRenderer from './ButtonCellRenderer.vue'

export default {
  components: { AgGridVue },
  mixins: [AgGridColumnMethods],
  props: {
    completed: Boolean,
    activeTab: String,
    pageLimit: Number,
    activeTabRows: Number,
    paginatedData: Array,
    parseTimestamp: Function,
    tableReady: Boolean,
    handleSelectionChange: Function,
    showLedgerItem: Function,
    busy: Boolean,
    invertPayments: Boolean,
    hasCreatePermission: Boolean
  },
  data () {
    return {
      gridKey: 0,
      columnDefs: [],
      rowData: [],
      gridApi: null,
      columnApi: null,
      components: null,
      gridOptions: {
        treeData: true,
        undoRedoCellEditing: true,
        singleClickEdit: true,
        groupDefaultExpanded: 0,
        defaultColDef: {
          resizable: true,
          sortable: true,
          unSortIcon: true
        },
        aggFuncs: {
          sum: this.customSum
        },
        groupRemoveSingleChildren: true,
        autoGroupColumnDef: {
          headerName: 'Reconciled',
          colId: 'reconciled',
          width: 120,
          sortable: true,
          checkboxSelection: !this.completed && this.hasCreatePermission,
          headerCheckboxSelection: !this.completed && this.hasCreatePermission,
          headerCheckboxSelectionFilteredOnly: true,
          valueGetter: (params) => {
            const node = params.node
            if (node && node.group && node.allLeafChildren && node.allLeafChildren.length) {
              return node.allLeafChildren.every(n => n.data && n.data.recon) ? 1 : 0
            }
            return params.data ? (params.data.recon ? 1 : 0) : null
          },
          valueFormatter: () => '',
          comparator: (_1, _2, nodeA, nodeB) => {
            const getRecon = (node) => {
              if (node.group && node.allLeafChildren && node.allLeafChildren.length) {
                return node.allLeafChildren.every(n => n.data && n.data.recon) ? 1 : 0
              }
              return node.data ? (node.data.recon ? 1 : 0) : 0
            }
            return getRecon(nodeA) - getRecon(nodeB)
          }
        },
        getDataPath: (data) => {
          let isMultiLine = false
          let matches = 0
          const ledgerKey = (r) => r.ledger_id || r.ledger
          for (let row of this.rowData) {
            if (row.vendor && data.vendor) {
              if (row.payment_confirmation_number === data.payment_confirmation_number && ledgerKey(row) === ledgerKey(data) && row.vendor === data.vendor) {
                matches++
              } else if (ledgerKey(row) === ledgerKey(data) && !row.payment_confirmation_number && !data.payment_confirmation_number && row.vendor === data.vendor) {
                matches++
              }
            } else {
              if (row.payment_confirmation_number === data.payment_confirmation_number && ledgerKey(row) === ledgerKey(data)) {
                matches++
              } else if (ledgerKey(row) === ledgerKey(data) && !row.payment_confirmation_number && !data.payment_confirmation_number) {
                matches++
              }
            }
          }
          if (matches > 1) {
            isMultiLine = true
          }
          if (data.type && data.type.includes('ap') && !data.number && !isMultiLine) {
            return [`${data.transaction_id}`, `${data.ledger_id}`]
          } else if (data.type && data.type.includes('ap') && (!data.number || data.number === '')) {
            return [`${data.ledger_id} && ${data.vendor}`, `line ${data.line_number}`]
          }
          let paymentTypes = ['EFT', 'ACH', 'MANUAL', 'WIRE', 'NA']
          if (data.payment_type) {
            if (paymentTypes.find(type => {
              return data.payment_type.toUpperCase().includes(type)
            })) {
              let formattedPaymentType = ''
              if (data.payment_type.includes('EFT') || data.payment_type.includes('ACH')) {
                formattedPaymentType = data.payment_type
                const ledger = data.ledger_id || data.ledger
                if (isMultiLine) {
                  return [`${data.number} - ${formattedPaymentType} && ${data.vendor}`, `${ledger} - line ${data.line_number} && ${data.vendor}`]
                } else {
                  return [`${data.number} - ${formattedPaymentType} && ${data.vendor}`, `${ledger}`]
                }
              } else {
                formattedPaymentType = data.payment_type.replace('_', ' ').toLowerCase().replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())))
              }
              if (formattedPaymentType.toUpperCase().includes('MANUAL')) {
                let words = formattedPaymentType.split(' ')
                formattedPaymentType = `${words[1]} ${words[0]}`
              }
              if (data.number) {
                const ledger = data.ledger_id || data.ledger
                if (isMultiLine) {
                  return [`${data.number} - ${formattedPaymentType} && ${data.vendor}`, `${ledger} - line ${data.line_number} && ${data.vendor}`]
                } else {
                  return [`${data.number} - ${formattedPaymentType} && ${data.vendor}`, `${ledger}`]
                }
              }
            }
          } else {
            if (isMultiLine) {
              return data.number ? [`${data.number} && ${data.vendor}`] : [`${data.ledger_id} - line ${data.line_number} && ${data.vendor}`]
            } else {
              return data.number ? [`${data.number}`, `${data.ledger_id || data.ledger}`] : [`${data.ledger_id || data.ledger}`]
            }
          }
        },
        onGridReady: params => {
          this.onGridReady(params)
        },
        onFirstDataRendered: params => {
          this.onFirstDataRendered(params)
        },
        onCellValueChanged: params => {
          this.onCellValueChanged(params)
        },
        getRowId: params => {
          // CP4.4: ag-grid v31 getRowNodeId(data) -> getRowId(params) with params.data; must return a string.
          return String(params.data.id)
        },
        rowClassRules: {
          'ag-changed': params => {
            return (params.data || {}).edited || false
          }
        }
      },
      ledgerGroupColumn: {
        headerName: 'Ledger ID',
        sort: 'asc',
        minWidth: 200,
        showRowGroup: true,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          padding: 20
        },
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        filterValueGetter: (params) => {
          if (!params.data) return ''
          const d = params.data
          if (d.type === 'checks' || d.type === 'payments') {
            return d.related_transactions_ledger_ids && d.related_transactions_ledger_ids.length ? d.related_transactions_ledger_ids.map(r => r.ledger_id).join(', ') : ''
          }
          return d.ledger_id || d.ledger || ''
        },
        valueGetter: (params) => {
          const d = params.data || {}
          if (d.type === 'checks' || d.type === 'payments') {
            return d.related_transactions_ledger_ids && d.related_transactions_ledger_ids.length ? d.related_transactions_ledger_ids.map(r => r.ledger_id).join(', ') : ''
          }
          if (!params.node || !params.node.key) return ''
          const key = params.node.key
          if (key.includes('&&')) {
            return key.split(' && ').slice(0, -1).join(' ')
          }
          return key
        },
        comparator: (_1, _2, c, d) => {
          const getVal = (node) => {
            const data = node.group && node.allLeafChildren && node.allLeafChildren.length
              ? node.allLeafChildren[0].data
              : node.data
            if (!data) return 0
            return parseInt(data.ledger_id || data.ledger) || 0
          }
          return getVal(c) - getVal(d)
        }
      },
      ledgerIdColumn: {
        headerName: 'Ledger ID',
        field: 'ledger_id',
        width: 200,
        hide: true,
        suppressColumnsToolPanel: true
      },
      checkColumn: {
        headerName: 'Payment #',
        aggFunc: 'first',
        valueGetter: (params) => {
          if (!params.data) return null
          return params.data.payment_confirmation_number || params.data.number || null
        },
        width: 110,
        resizable: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        comparator: (check1, check2) => {
          if (!check1) check1 = ''
          if (!check2) check2 = ''
          if (check1 && check2) {
            return check1.localeCompare(check2, 'en', { numeric: true })
          }
          return check1.localeCompare(check2)
        }
      },
      timePostedColumn: {
        headerName: 'Posted on',
        field: 'transaction_date',
        aggFunc: 'first',
        width: 150,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        comparator: (date1, date2) => {
          let dateObj1 = new Date(date1)
          let dateObj2 = new Date(date2)
          return dateObj1.getTime() - dateObj2.getTime()
        }
      },
      vendorColumn: {
        headerName: 'Vendor',
        field: 'vendor_name',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        }
      },
      descriptionColumn: {
        headerName: 'Description',
        field: 'meta_name',
        width: 300,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        filterValueGetter: (params) => {
          let valueString = ''
          if (params.data && params.data.meta_name) {
            valueString = params.data.meta_name
          }
          if (valueString) {
            let words = valueString.split(' ')
            let returnString = ''
            for (let index in words) {
              if (index > 3) {
                returnString = returnString + words[index] + ' '
              }
            }
            return returnString
          }
        },
        valueFormatter: (params) => {
          let valueString = params.value
          if (valueString) {
            let words = valueString.split(' ')
            let returnString = ''
            for (let index in words) {
              if (index > 3) {
                returnString = returnString + words[index] + ' '
              }
            }
            return returnString
          }
        },
        comparator: (_1, _2, c, d) => {
          let data1 = c.group ? c.allLeafChildren[0].data.meta_name : c.data.meta_name
          let data2 = d.group ? d.allLeafChildren[0].data.meta_name : d.data.meta_name
          return data1.localeCompare(data2, undefined, { numeric: true })
        }
      },
      amountColumn: {
        headerName: 'Amount',
        field: 'amount_decimal',
        aggFunc: 'sum',
        type: 'rightAligned',
        width: 150,
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true
        },
        valueFormatter: (params) => {
          let value = params.value
          if ((this.activeTab === 'payments' || this.activeTab === 'checks') && this.invertPayments) value = parseFloat(value * -1).toFixed(2)
          else value = parseFloat(value).toFixed(2)
          return this.intlNumberFormatter(value)
        },
        comparator: (amt1, amt2) => {
          if (amt1 && amt2) {
            return amt1 - amt2
          }
          return amt1.localeCompare(amt2, undefined, { numeric: true })
        }
      },
      viewColumn: {
        headerName: 'View',
        sortable: false,
        unSortIcon: false,
        suppressMovable: true,
        cellRenderer: 'btnCellRenderer',
        cellRendererParams: {
          showLedgerItem: this.showLedgerItem
        }
      }
    }
  },
  methods: {
    /**
     * Formats dollar amounts with thousands separators
     * @param value
     * @return {*}
     */
    formatAmount (value) {
      return value ? formatLocale(value) : ''
    },
    onGridReady (params) {
      if (params) {
        this.gridApi = params.api
        this.columnApi = params.api
        this.$emit('onNewGrid', params)
      }
    },
    onFirstDataRendered (params) {
      this.loaded = true
      // Restore the saved (reconciled) selection now that the tree row nodes actually exist. On the
      // FIRST render restoreSelection() runs too early (onGridReady fires before the grid's rows are
      // created, so getRowNode returns nothing); doing it here means reconciled rows are selected on
      // initial load, not only after a tab round-trip. The group-selection pass below then reflects it.
      this.$emit('firstDataRendered', params)
      this.gridApi.forEachNode(node => {
        node.setExpanded(false)
        if (node.allLeafChildren && node.allLeafChildren.length && node.allLeafChildren.every(child => child.isSelected())) {
          node.setSelected(true) // ag-grid v31: node.selected is read-only; selection must go through setSelected()
        }
      })
      params.api.redrawRows()
    },
    onCellValueChanged (params) {
      // Check if any change occurred so we can update the corresponding resource
      if (params.value && params.oldValue === params.newValue) return

      const rowNode = this.gridApi.getDisplayedRowAtIndex(params.rowIndex)

      // Mark data as changed for css purposes
      params.data.edited = true
      this.redrawCurrentRow(rowNode)

      this.setCellFocus()
    }
  },
  watch: {
    paginatedData () {
      this.rowData = this.paginatedData
      this.gridKey++
    },
    invertPayments () {
      this.gridApi.refreshCells({
        columns: ['amount_decimal'],
        force: true
      })
    }
  },
  beforeMount () {
    this.components = {
      btnCellRenderer: ButtonCellRenderer
    }
  },
  mounted () {
    this.rowData = this.paginatedData
    this.columnDefs.push(this.ledgerGroupColumn)
    this.columnDefs.push(this.ledgerIdColumn)
    this.columnDefs.push(this.checkColumn)
    this.columnDefs.push(this.timePostedColumn)
    this.columnDefs.push(this.vendorColumn)
    this.columnDefs.push(this.descriptionColumn)
    this.columnDefs.push(this.amountColumn)
    this.columnDefs.push(this.viewColumn)
  }
}
</script>

<style>
@import 'ag-grid-community/styles/ag-grid.css';
  @import 'ag-grid-community/styles/ag-theme-balham.css';

  ag-grid {
    width: 100%;
    height: 540px;
    background: #ffffff;
    font-family: "Helvetica", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: left;
  }
  .default-grid { float: left; width: 99%; height: 800px; }
</style>
